import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
} from "aws-amplify/auth";
import {
  AUTH_USER,
  AUTH_MFA,
  AUTH_NEW_PASSWORD_REQUIRED,
  UNAUTH_USER,
  REGISTER_USER,
  REGISTER_USER_CONFIRM,
  REGISTER_MFA,
  REGISTER_USER_ERROR,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_CONFIRM,
  AUTH_ERROR,
  RESET_APP
} from "./types";
import { getPortalDetails } from "./portal_actions";
import { patch, post } from "./api";
import { ConsoleLogger, Cache } from "aws-amplify/utils";

const logger = new ConsoleLogger("AUTH.ACTIONS");

export function authError(error) {
  return {
    type: AUTH_ERROR,
    payload: error.message ? error.message : error
  };
}

// Cognito - Auth.signIn()
export function login({ username, password }, navigate) {
  return async function(dispatch) {
    try {
      logger.info("actions.login(): username:", { username });
      await Cache.clear();
      await Cache.removeItem("lastpath");

      const { nextStep } = await signIn({ username, password });

      logger.info("login():signIn() nextStep:", nextStep);

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        dispatch({ type: AUTH_NEW_PASSWORD_REQUIRED, payload: { username } });
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE' || nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        dispatch({ type: AUTH_MFA, payload: { username } });
      } else if (nextStep.signInStep === 'DONE') {
        const [currentAuthUser, session] = await Promise.all([
          getCurrentUser(),
          fetchAuthSession(),
        ]);
        const payload = session.tokens.accessToken.payload;
        dispatch({ type: AUTH_USER, currentAuthUser, session: { payload } });
        dispatch(getPortalDetails());
        await patch(`/user/updateLastAuth`, {}).catch(err => {
          logger.error("login():updateLastAuth err:", err);
        });
        // navigate("/portal"); might be causing default nav lock issue
      }
    } catch (err) {
      logger.error("login():signIn() err:", err);
      if (err.name === "UserNotConfirmedException") {
        dispatch({ type: REGISTER_MFA, payload: { user: { username } } });
        navigate("/signup");
      } else {
        dispatch(authError(err));
      }
    }
  };
}

// Cognito - getCurrentUser() & fetchAuthSession()
// This is a pass-through function to indicate that user has already authenticated
// and has a valid Amplify session.

export function validateUserSession(bypass = true) { // eslint-disable-line
  return function(dispatch) {
    logger.info("validateUserSession()");
    const checkSession = async () => {
      try {
        const [currentAuthUser, session] = await Promise.all([
          getCurrentUser(),
          fetchAuthSession(),
        ]);
        const payload = session.tokens.accessToken.payload;
        const idToken = session.tokens.idToken.payload;
        dispatch({ type: AUTH_USER, currentAuthUser: {...currentAuthUser, attributes: idToken}, session: { payload } });
       
        logger.info("validateUserSession(): session is valid.");
        logger.info("validateUserSession(): currentAuthUser:", currentAuthUser);

        dispatch(getPortalDetails());
        const credentials = session.credentials;
        if (credentials) {
          logger.warn("ACCESSKEYID:", credentials.accessKeyId);
          logger.warn("SESSIONTOKEN:", credentials.sessionToken);
          logger.warn("SECRET ACCESS KEY:", credentials.secretAccessKey);
        }

      } catch (err) {
        // This is the expected exception when no user is logged in.
        // We can log it for info but avoid treating it as a critical error.
        if (err.name === 'UserUnAuthenticatedException') {
          logger.info("validateUserSession(): No active session found. User is not authenticated.");
        } else {
          // Log other, unexpected errors.
          logger.error("validateUserSession() unexpected error:", err);
        }
        await Cache.clear();
        dispatch({ type: UNAUTH_USER });
        dispatch({ type: RESET_APP });
      }
    };
    checkSession();
  };
}

// Cognito - confirmSignIn() for new password
export function setNewPassword(cognitoUser, newPassword, navigate) {
  return async function(dispatch) {
    logger.info("setNewPassword(): cognitoUSer, newPassword:", {
      cognitoUser,
      newPassword
    });

    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: newPassword });
      logger.info("setNewPassword():confirmSignIn() nextStep: ", nextStep);

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_MFA_CODE') {
        dispatch({ type: AUTH_MFA, payload: { username: cognitoUser.username } });
      } else if (nextStep.signInStep === 'DONE') {
        const [currentAuthUser, session] = await Promise.all([
          getCurrentUser(),
          fetchAuthSession(),
        ]);
        const payload = session.tokens.accessToken.payload;
        dispatch({ type: AUTH_USER, currentAuthUser, session: { payload } });
        dispatch(getPortalDetails());
        navigate("/");
      }
    } catch (err) {
      logger.error("setNewPassword():confirmSignIn() err:", err);
      dispatch(authError(err));
    }
  };
}

// Cognito - signOut()
export function logout(username = "", navigate, message) {
  return async function(dispatch) {
    logger.info("logout(): username: ", username);
    await Cache.removeItem("lastpath");
    try {
      await signOut();
      logger.info("logout():signOut() success");
      await Cache.clear();
      dispatch({ type: UNAUTH_USER });
      dispatch({ type: RESET_APP });

      if (message === 'inactivity') {
        window.location.assign("/login?message=inactivity");
      } else {
        navigate("/");
        window.location.reload();
      }
    } catch (err) {
      logger.error("logout():signOut() err:", err);
      dispatch(authError(err));
    }
  };
}

// Cognito - confirmSignIn() for MFA
export function confirmLogin({ cognitoUser, code }, navigate) {
  return async function(dispatch) {
    logger.info("confirmLogin(): cognitoUSer, code:", {
      cognitoUser,
      code
    });

    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: code });
      logger.info("confirmLogin():confirmSignIn() nextStep: ", nextStep);

      if (nextStep.signInStep === 'DONE') {
        const [currentAuthUser, session] = await Promise.all([
          getCurrentUser(),
          fetchAuthSession(),
        ]);
        const payload = session.tokens.accessToken.payload;
        dispatch({ type: AUTH_USER, currentAuthUser, session: { payload } });
        dispatch(getPortalDetails()); // This might need to be called after successful login, not after MFA confirmation
        navigate("/");
      }
    } catch (err) {
      logger.error("confirmLogin():confirmSignIn() err:", err);
      dispatch(authError(err));
    }
  };
}

// Cognito - signUp()
export function register({ username, password, email, phone }, navigate) {
  return async function(dispatch) {
    logger.info("register(): username, password, email, phone: ", {
      username,
      password,
      email,
      phone
    });

    try {
      const { nextStep } = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
            phone_number: phone,
          },
        }
      });

      logger.info("register():signUp() nextStep:", nextStep);

      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        dispatch({ type: REGISTER_MFA, payload: { user: { username } } });
      } else if (nextStep.signUpStep === 'DONE') {
        dispatch({ type: REGISTER_USER });
        navigate("/");
      }
    } catch (err) {
      logger.error("register():signUp() err:", err);
      dispatch(authError(err));
    }
  };
}

// Cognito - confirmSignUp()
export function confirmRegistration({ cognitoUser, code }, navigate) {
  return async function(dispatch) {
    logger.info("confirmRegistration(): cognitoUSer, code:", {
      cognitoUser,
      code
    });
    const { username } = cognitoUser.user;

    try {
      const { nextStep } = await confirmSignUp({ username, confirmationCode: code });
      logger.info("confirmRegistration():confirmSignUp() nextStep: ", nextStep);

      if (nextStep.signUpStep === 'DONE') {
        dispatch({ type: REGISTER_USER_CONFIRM });
        navigate("/");
      }
    } catch (err) {
      logger.error("confirmRegistration():confirmSignUp() err:", err);
      dispatch({
        type: REGISTER_USER_ERROR,
        payload: err.message,
        cognitoUser
      });
    }
  };
}

// Cognito - resendSignUpCode()
export function resendConfirmationCode({ cognitoUser }, navigate) {
  return async function(dispatch) {
    logger.info("resendConfirmationCode(): username: ", {
      cognitoUser
    });
    const { username } = cognitoUser.user;

    try {
      await resendSignUpCode({ username });
      logger.info("resendConfirmationCode():resendSignUp() success");
      dispatch({ type: REGISTER_MFA, payload: cognitoUser });
    } catch (err) {
      logger.error("resendConfirmationCode():resendSignUp() err:", err);
      dispatch(authError(err));
    }
  };
}

// Cognito - resetPassword()
export function forgotPassword({ username }, navigate) {
  return async function(dispatch) {
    logger.info("forgotPassword(): username: ", { username });

    try {
      const { nextStep } = await resetPassword({ username });
      logger.info("forgotPassword():resetPassword() nextStep:", nextStep);
      if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        dispatch({ type: FORGOT_PASSWORD });
      }
    } catch (err) {
      logger.error("forgotPassword():resetPassword() err:", err);
      dispatch(authError(err));
    }
  };
}

// Cognito - confirmResetPassword()
export function confirmForgotPassword(
  { username, confirmationCode, newPassword }, // eslint-disable-line
  navigate
) {
  return async function(dispatch) {
    logger.info("confirmForgotPassword(): username, code, newPassword: ", {
      username,
      confirmationCode,
      newPassword
    });
    console.log(username, confirmationCode, newPassword)
    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
      logger.info("confirmForgotPassword():confirmResetPassword() success");
      dispatch({ type: FORGOT_PASSWORD_CONFIRM }); // This might need to be called after successful login, not after MFA confirmation
      navigate("/");
    } catch (err) {
      logger.error("confirmForgotPassword():confirmResetPassword() err:", err);
      dispatch(authError(err));
    }
  };
}


export const createCheckout = (seatOption, creditOption, recaptchaValue) => {
  return async (dispatch) => {
    try {
      const checkoutResponse = await post("/createcheckout", {
        seat: seatOption,
        credits: creditOption,
        captcha: recaptchaValue
      });
      if (checkoutResponse){
        if(checkoutResponse.success && checkoutResponse.session){
          return {url : checkoutResponse.session.url}
        }
        else if(checkoutResponse.error){
          return {error : checkoutResponse.session.url}
        }
      }
    } catch (e) {
      logger.error("createCheckout():", e);
    }
    return {error : "Unknown Error"}
  }
}